<template>
  <div class="audio-player" :class="{ disabled: disabled }">
    <div class="audio-content">
      <div class="player-actions">
        <div
          class="play-btn"
          @click="toggle_play"
          :class="{
            pause:
              player && !player.paused && player.currentTime !== player.duration
          }"
        ></div>
      </div>

      <div
        class="audio-wrapper player-container"
        :id="'player-container_' + timestamp"
        href="javascript:;"
      >
        <audio
          class="player"
          :id="'player_' + timestamp"
          @timeupdate="init_progress_bar"
          @waiting="waiting"
          @playing="playing"
          @loadedmetadata="loadedmetadata"
        >
          <source :src="src" />
        </audio>
      </div>

      <div class="player-controls">
        <div v-if="show_title" class="audio-name">{{ name }}</div>
        <div class="progress-bar" @click="seek" ref="progress">
          <div class="progress-line" :style="{ width: progress + '%' }"></div>
        </div>
      </div>
    </div>
    <div class="player-duration">
      {{
        player && player.duration
          ? calculate_current_value(player.duration - player.currentTime)
          : ''
      }}
    </div>
  </div>
</template>

<script>
  export default {
    name: 'vaudio-player',

    props: {
      src: {
        type: String,
        default: ''
      },

      name: {
        type: String,
        default: ''
      },

      disabled: {
        type: Boolean,
        default: false
      },

      show_title: {
        type: Boolean,
        default: true
      }
    },

    data() {
      return {
        player: null,
        progress: 0,
        is_playing: false,
        duration: null,
        timestamp: '0'
      };
    },

    methods: {
      init_progress_bar() {
        this.player = document.getElementById('player_' + this.timestamp);
        const progress = (this.player.currentTime / this.player.duration) * 100;
        this.progress = progress;
        if (progress === 100) {
          this.$emit('end');
        }
      },

      loadedmetadata(e) {
        this.duration = e && e.target ? e.target.duration : 0;
        this.$emit('change_duration', this.duration);
      },

      waiting(e) {
        this.$emit('waiting', e);
      },

      seek(event) {
        if (this.disabled) {
          return;
        }

        var percent = event.offsetX / this.$refs.progress.offsetWidth;
        this.player.currentTime = percent * this.player.duration;
        this.progress = percent / 100;
      },

      playing(e) {
        this.$emit('playing');
      },

      toggle_play() {
        if (!this.player || this.disabled) {
          return;
        }

        if (this.player.paused === false) {
          this.$emit('pause');
          this.player.pause();
          this.is_playing = false;
        } else {
          this.$emit('play');
          this.player.play();
          this.is_playing = true;
        }
      },

      calculate_current_value(currentTime) {
        var current_hour = parseInt(currentTime / 3600) % 24,
          current_minute = parseInt(currentTime / 60) % 60,
          current_seconds_long = currentTime % 60,
          current_seconds = current_seconds_long.toFixed(),
          current_time =
            (current_minute < 10 ? '0' + current_minute : current_minute) +
            ':' +
            (current_seconds < 10 ? '0' + current_seconds : current_seconds);

        return current_time;
      }
    },

    beforeMount() {
      const date = new Date();
      this.timestamp = date.getTime();
    },

    mounted() {
      this.player = document.getElementById('player_' + this.timestamp);
    }
  };
</script>

<style lang="scss">
@import '@/styles/variables';

.audio-content {
  display: flex;
  align-items: center;
}
.audio-player {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  &.disabled {
    opacity: 0.7;

    & * {
      cursor: not-allowed;
    }
  }

  .player-controls {
    justify-content: flex-start;
    flex-direction: column;
    display: flex;
    flex: 1;

    progress {
      width: 100%;
      border: none;
      position: relative;
    }

    .progress-bar {
      position: relative;
      width: 100%;
      height: 1px;
      background: #d2d6e0;
      cursor: pointer;
    }

    .progress-line {
      position: absolute;
      top: 0;
      left: 0;
      width: 40%;
      height: 1px;
      background: $green;

      &:after {
        content: '';
        position: absolute;
        right: -5px;
        top: calc(50% - 5px);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: $green;
      }
    }

    progress[value] {
      position: relative;
      -webkit-appearance: none;
      appearance: none;
      background: #d2d6e0;
      color: $green;
      height: 5px;
      border: none;

      &:after {
        content: '';
        position: absolute;
        right: -10px;
        top: calc(50% - 5px);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: $green;
      }
    }

    progress[value]::-webkit-progress-bar {
      position: relative;
      background-color: #d2d6e0;
      border-radius: 2px;
      border: none;
      color: $green;

      &:after {
        content: '';
        position: absolute;
        right: -10px;
        top: calc(50% - 5px);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: $green;
      }
    }

    progress::-webkit-progress-value {
      position: relative;
      background-color: $green;
      border: none;

      &:after {
        content: '';
        position: absolute;
        right: -10px;
        top: calc(50% - 5px);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: $green;
      }
    }
  }

  .play-btn {
    background-image: url('/static/icons/play-icon.svg');
    background-size: cover;
    width: 50px;
    height: 50px;
    cursor: pointer;
    &.pause {
      background-image: url('/static/icons/pause-icon.svg');
    }
  }
}

.audio-name {
  margin-bottom: 5px;
  font-size: 13px;
  line-height: 19px;
  letter-spacing: 0.005em;
  color: #9faab6;
}

.player-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 20px;
}

.player-duration {
  font-size: 13px;
  line-height: 19px;
  letter-spacing: 0.005em;
  color: #9faab6;
  margin-top: 5px;
  width: 50px;
  text-align: center;
}
</style>
